import { useEffect, useRef, useState } from 'react';
import PlayPause from '@components/PlayPause.tsx';
import attribute from '@/utils/attribute.ts';
import hexToRgba from '@/utils/hexToRgba.ts';
import Scheduler from '@components/gems/Scheduler.tsx';
import { AnimatePresence, motion } from 'framer-motion';
import { PageData } from '@/gem.types';

interface Props {
  gem?: any;
  theme: PageData['theme'];
  attribution?: string;
  mediaEndpoint: string;
  gemEndpoint: string;
  calendarEndpoint: string;
  isVideo?: boolean;
  index?: number;
}

const GemElement = ({
  gem,
  theme,
  attribution,
  mediaEndpoint,
  gemEndpoint,
  calendarEndpoint,
  isVideo,
  index
}: Props) => {
  const [playerState, setPlayerState] = useState<'playing' | 'paused'>('paused');
  const imageRef = useRef<HTMLImageElement>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);

  let url: string = '#';
  let image: string = '';

  useEffect(() => {
    if (viewerOpen) {
      window.dispatchEvent(
        new CustomEvent('viewerStateChange', { detail: { active: true, index } })
      );
    } else {
      window.dispatchEvent(
        new CustomEvent('viewerStateChange', { detail: { active: false, index } })
      );
    }
  }, [viewerOpen]);

  if (gem) {
    if (gem.type === 'interactive') {
      if (gem.firstScenePlaybackId) {
        image = `https://image.mux.com/${gem.firstScenePlaybackId}/animated.gif`;
      }
      if (gem.project?.scenes?.length) {
        if (gem.project.scenes[0]?.video?.playbackId) {
          image = `https://image.mux.com/${gem.project.scenes[0].video.playbackId}/animated.gif`;
        }
      }
    }

    if (gem.type === 'video') {
      image = `https://image.mux.com/${gem.playbackId}/animated.gif`;
    }

    if (gem.type === 'image' && gem.image) {
      image = `${mediaEndpoint}/${gem?.optimized || gem.image}`;
    }

    if (gem.type === 'embed' || gem.type === 'link') {
      image = gem.image as string;
    }

    if (gem.type === 'product') {
      image = gem.uri as string;
    }
  }

  if (gem?.type === 'link' || gem?.type === 'product') {
    url = attribute(gem.url, attribution || '') || '#';
  } else {
    url = `/${gem?.shortId}?ref=${gem?.gemId}`;
  }

  if (gem?.type === 'interactive') {
    url = `${gemEndpoint}/${gem?.shortId}`;
  }

  const handlePlayPauseClick = () => {
    setPlayerState('playing');
    setTimeout(() => {
      setPlayerState('paused');
    }, 1000);
  };

  if (!gem) return null;

  const handleGemClick = () => {
    if (['video', 'embed', 'interactive'].includes(gem?.type)) {
      setViewerOpen(true);
      setIframeUrl(url);
    } else {
      if (gem.type === 'image') {
        window.open(image, '_blank');
      }
      window.location.href = url;
    }
  };

  const renderPreview = () => {
    if (gem.type === 'product') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      const imageToRender = gem.ogImage || gem.uri;
      if (!imageToRender) {
        return (
          <div
            className="mb-2 font-semibold cursor-pointer"
            style={{ color: theme.containerTextColor }}
          >
            {gem.name}
          </div>
        );
      }
    }

    return (
      <>
        {!['video', 'image', 'interactive'].includes(gem.type) && (
          <h1
            className="mb-6 text-3xl font-bold text-center w-full leading-tight text-black"
            style={{ color: theme?.containerTextColor }}
          >
            {gem.name}
          </h1>
        )}
        <div
          className="relative w-full flex items-center justify-center"
          style={{
            backgroundImage: `url(${image})`,
            backgroundSize: ['video'].includes(gem.type) ? 'contain' : 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundColor: theme.backgroundColor,
            height: window.innerHeight * 0.35,
            cursor: gem.type === 'image' ? 'pointer' : 'default',
            maxHeight: '70vh'
          }}
        >
          <img ref={imageRef} src={image} alt="" style={{ display: 'none' }} />
          {!['video', 'embed', 'interactive'].includes(gem.type) ? null : (
            <PlayPause
              state={playerState}
              onClick={handlePlayPauseClick}
              height={160}
              width={160}
            />
          )}
        </div>
      </>
    );
  };

  if (gem?.type === 'link' || gem?.type === 'page' || isVideo) {
    let emoji: string = '🔗';

    if (isVideo) {
      if (gem?.type === 'video') {
        emoji = '🎬';
      }
      if (gem?.type === 'product') {
        emoji = '📦';
      }
      if (gem?.type === 'embed') {
        emoji = '🎬';
      }
      if (gem?.type === 'interactive') {
        emoji = '🎬';
      }
      if (gem?.type === 'scheduler') {
        emoji = '🕘';
      }
      if (gem?.type === 'text') {
        emoji = '📝';
      }
    }

    let title = gem.name;

    if (gem?.type === 'product') {
      title = `${gem.name}`;
    }

    if (gem?.type === 'embed' || gem?.type === 'interactive' || gem?.type === 'video') {
      title = `${gem.name}`;
    }

    if (gem?.type === 'scheduler') {
      title = `${gem.name}`;
    }

    return (
      <button
        onClick={() => (window.location.href = url)}
        className="relative flex items-center justify-center w-full py-2 px-4 mb-6 bg-white  border-2 rounded-full transition-transform transform hover:scale-105"
        style={{
          borderColor: theme?.primaryColor,
          backgroundColor: hexToRgba(theme.backgroundColor, 0.8),
          zIndex: 1
        }}
      >
        <div
          className="absolute left-4 flex items-center justify-center h-8 w-8 rounded-full bg-primary"
          style={{ backgroundColor: theme?.primaryColor, color: theme?.buttonTextColor }}
        >
          <span className="font-semibold pt-1">{emoji}</span>
        </div>
        <span className="text-lg font-semibold" style={{ color: theme?.textColor }}>
          {title}
        </span>
      </button>
    );
  }

  if (gem.type === 'text') {
    return (
      <div
        className="prose lg:prose-xl mt-2 px-8 py-6 mb-10 rounded-2xl w-full bg-white"
        style={{
          border: `2px solid ${theme.borderColor}`
        }}
      >
        <h3>{gem.name}</h3>
        <div dangerouslySetInnerHTML={{ __html: gem.text }} />
      </div>
    );
  }

  if (gem.type === 'scheduler') {
    return (
      <div>
        <Scheduler gem={gem} endpoint={calendarEndpoint} inPage={true} theme={theme} />
      </div>
    );
  }

  if (gem.type === 'image') {
    return (
      <div className="transition-all duration-300 ease-in-out w-full mb-10 cursor-pointer">
        {renderPreview()}
      </div>
    );
  }

  const renderViewer = () => {
    return (
      <AnimatePresence>
        {viewerOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed top-0 left-0 w-screen h-screen flex items-center justify-center z-50"
            style={{
              backgroundColor: theme.backgroundColor
            }}
          >
            <iframe src={iframeUrl || ''} width="100%" height="100%" allowFullScreen />
            <button
              className="absolute top-2 right-2 bg-white border-none rounded-full w-8 h-8 flex items-center justify-center cursor-pointer z-50"
              onClick={() => setViewerOpen(false)}
            >
              x
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  if (['embed', 'interactive', 'video'].includes(gem.type)) {
    return (
      <>
        <div
          className="transition-all duration-300 ease-in-out w-full mb-10 cursor-pointer"
          onClick={handleGemClick}
        >
          {renderPreview()}
        </div>
        {renderViewer()}
      </>
    );
  }

  return (
    <>
      <div
        className="transition-all duration-300 ease-in-out p-4 w-full px-8 py-6 mb-10 shadow-lg rounded-2xl cursor-pointer z-10"
        style={{
          border: `2px solid ${theme.borderColor}`,
          backgroundColor: theme.containerColor,
          zIndex: 1
        }}
        onClick={handleGemClick}
      >
        {renderPreview()}
      </div>
      {renderViewer()}
    </>
  );
};

export default GemElement;
